"use client";

import { login } from "@/actions/auth.action";
import config from "@/app.config";
import routes from "@/app.route";
import OTPInput from "@/components/otp-input";
import { getMessage } from "@/lib/error";
import useAuth from "@/stores/auth";
import useToast from "@/stores/toast";
import { Box, Button, InputAdornment, TextField } from "@mui/material";
import axios from "axios";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";

export default function SignIn() {
  const {
    digits,
    error,
    state,
    loading,
    changePhone,
    otpSent,
    resetOtp,
    setOtp,
    setError,
    setPhone,
    setLoading,
  } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { toast } = useToast();
  const requestOtpHandler = async () => {
    try {
      setLoading(true);

      await axios.post(
        `${config.app.apiBaseUrl}/api/v2/request-otp`,
        {
          phone: state.phone,
        },
        {
          headers: {
            Accept: "application/json",
          },
          withCredentials: true,
        },
      );

      toast({
        error: false,
        message: `Verification code sent to ${state.phone}`,
      });

      otpSent();
    } catch (error: unknown) {
      setError(getMessage(error));
    } finally {
      setLoading(false);
    }
  };
  const verifyOtpHandler = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        `${config.app.apiBaseUrl}/api/v2/verify-otp`,
        {
          phone: state.phone,
          otp: state.otp,
        },
        {
          headers: {
            Accept: "application/json",
          },
          withCredentials: true,
        },
      );

      await login(response.data);

      toast({
        error: false,
        message: "OTP Verified",
      });

      resetOtp();

      const ret = searchParams.get("ret");

      router.push(ret ? ret : routes.home.url);
    } catch (error: unknown) {
      setError(getMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="mb-8">
        {state.otpSent ? (
          <div className="mx-auto max-w-[200px]">
            <p className="text-center text-sm">
              <span>Please enter the OTP sent to {state.phone}. </span>
              <Button variant="text" onClick={changePhone}>
                Change
              </Button>
            </p>
          </div>
        ) : (
          <TextField
            fullWidth
            error={!!error}
            helperText={error}
            value={state.phone}
            onChange={(event) => setPhone(event.target.value)}
            disabled={state.otpSent}
            label={state.otpSent ? "Mobile number" : "Enter Mobile number"}
            slotProps={{
              input: {
                startAdornment: state.phone.length > 0 && (
                  <InputAdornment position="start">
                    <Box
                      sx={{
                        pr: 1.5,
                        mr: 1,
                        borderRight: 1,
                        borderColor: "divider",
                        color: "text.secondary",
                        fontSize: "0.9rem",
                      }}
                    >
                      +91
                    </Box>
                  </InputAdornment>
                ),
                endAdornment: state.otpSent && (
                  <InputAdornment position="end">
                    <Button variant="text" onClick={changePhone}>
                      Change?
                    </Button>
                  </InputAdornment>
                ),
              },
            }}
          />
        )}
      </div>

      {state.otpSent && (
        <div className="mt-12 mb-6">
          <OTPInput
            error={error}
            length={digits}
            value={state.otp}
            onChange={setOtp}
          />
        </div>
      )}

      {!state.otpSent && (
        <p className="mb-3 text-xs text-zinc-500">
          By continuing, you agree to {config.app.name}&nbsp;
          <Link className="text-primary" href={routes.terms.url}>
            Terms of Use
          </Link>
          &nbsp;and&nbsp;
          <Link className="text-primary" href={routes.privacy.url}>
            Privacy Policy
          </Link>
          .
        </p>
      )}

      <div className="flex flex-col gap-y-4">
        {state.otpSent ? (
          <Button
            loading={loading}
            variant="contained"
            onClick={verifyOtpHandler}
          >
            Verify
          </Button>
        ) : (
          <Button
            loading={loading}
            variant="contained"
            onClick={requestOtpHandler}
          >
            Request OTP
          </Button>
        )}

        <p className="text-center text-sm">
          <span className="text-zinc-500">Not received your code? </span>
          <Button
            loading={loading}
            variant="text"
            onClick={() => {
              resetOtp();
              requestOtpHandler();
            }}
          >
            Resend code
          </Button>
        </p>
      </div>
    </>
  );
}
